import React from "react";
import { Star, Quote } from "lucide-react";

interface Testimonial {
  number: string;
  name: string;
  role: string;
  text: string;
  rating: number;
}

const testimonials: Testimonial[] = [
  {
    number: "01",
    name: "Marie Dupont",
    role: "Citoyenne engagée",
    rating: 5,
    text: "Grâce à cette application, j'ai pu signaler une fuite d'eau qui durait depuis des semaines. La mairie a réagi en 48h !"
  },
  {
    number: "02",
    name: "Thomas Martin",
    role: "Responsable municipal",
    rating: 5,
    text: "Un outil indispensable pour notre commune. Nous pouvons désormais prioriser les interventions selon les besoins réels des citoyens."
  },
  {
    number: "03",
    name: "Sophie Laurent",
    role: "Membre d'association",
    rating: 4,
    text: "Notre collectif utilise l'application pour coordonner les actions de nettoyage. La fonction collaborative est particulièrement utile."
  }
];

const stars = (rating: number) =>
  Array(5).fill(0).map((_, i) => (
    <Star
      key={i}
      size={14}
      strokeWidth={1.5}
      className={
        i < rating
          ? "text-[var(--ed-gold)] fill-[var(--ed-gold)]"
          : "text-[var(--ed-rule-strong)]"
      }
    />
  ));

const TestimonialsSection = () => {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="max-w-2xl mb-16 md:mb-24">
        <span className="ed-eyebrow mb-5 block">04 — Témoignages</span>
        <h2 className="ed-heading text-3xl md:text-5xl mb-5">
          Ils <span className="accent">témoignent</span>
        </h2>
        <p className="ed-subtle text-base md:text-lg leading-relaxed max-w-lg">
          Citoyens, élus, associations — une lecture éditoriale des retours qui façonnent la plateforme.
        </p>
      </div>

      {/* Timeline — editorial single column */}
      <div className="relative">
        {/* Central spine */}
        <div className="absolute left-[27px] md:left-[31px] top-2 bottom-2 w-px bg-gradient-to-b from-[var(--ed-navy-500)] via-[var(--ed-rule)] to-transparent opacity-40" />

        <div className="space-y-16 md:space-y-20">
          {testimonials.map((t, idx) => (
            <div
              key={t.number}
              className="relative flex gap-6 md:gap-10 group ed-fade-up"
              style={{ animationDelay: `${idx * 120}ms` }}
            >
              {/* Step marker */}
              <div className="relative z-10 shrink-0">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[var(--ed-paper)] border border-[var(--ed-rule-strong)] flex items-center justify-center shadow-sm group-hover:border-[var(--ed-navy-500)] group-hover:shadow-md transition-all duration-500">
                  <Quote
                    size={18}
                    strokeWidth={1.5}
                    className="text-[var(--ed-navy-500)]"
                  />
                </div>
              </div>

              {/* Content card */}
              <div className="pt-1 md:pt-2 flex-1">
                <blockquote className="font-display text-xl md:text-2xl leading-relaxed text-[var(--ed-ink)] italic mb-5 max-w-xl">
                  "{t.text}"
                </blockquote>

                <div className="flex items-center gap-4 pt-5 border-t border-[var(--ed-rule)]">
                  <div className="w-10 h-10 rounded-full bg-[var(--ed-navy-800)] text-white flex items-center justify-center font-display text-xs tracking-wider">
                    {t.name.split(" ").map((n) => n[0]).join("")}
                  </div>
                  <div className="flex-1">
                    <div className="font-display font-semibold text-sm text-[var(--ed-ink)]">
                      {t.name}
                    </div>
                    <div className="text-xs text-[rgba(10,15,31,0.55)]">
                      {t.role}
                    </div>
                  </div>
                  <div className="flex gap-0.5">{stars(t.rating)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Editorial rule */}
      <div className="mt-16 md:mt-24 flex items-center gap-6">
        <div className="h-px w-16 bg-[var(--ed-navy-500)]" />
        <span className="text-xs text-[rgba(10,15,31,0.40)] font-body tracking-wide">
          76 % de résolution en moins de 30 jours
        </span>
      </div>
    </div>
  );
};

export default TestimonialsSection;
